// Graph explorer — an expand-on-demand view of an asset's relationships.
//
// Phase 1: assets only, zero backend changes. The page starts from a focal
// asset and lets the user pull neighbors in one hop at a time (typed relations,
// parent, children), keeping the on-screen node count bounded by design. All
// API→graph normalization lives in components/graph/adapter.ts; this route owns
// fetch orchestration, URL state (?focus=&expanded=), layout and rendering.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    createFileRoute,
    useNavigate,
    useParams,
    useSearch,
} from '@tanstack/react-router'
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    Controls,
    ControlButton,
    MarkerType,
    useReactFlow,
    type Node as RFNode,
    type Edge as RFEdge,
    type NodeChange,
    type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../../components/ui/icon'
import { mdiGraphOutline, mdiMagnify, mdiAutoFix } from '@mdi/js'

import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useApi } from '../../api/hooks'
import { useAssetTypes } from '../../hooks/useQueries'
import { useDebounce } from '../../hooks/useDebounce'
import { Button } from '../../components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../components/ui/command'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog'
import { AssetNode, type AssetNodeData } from '../../components/graph/AssetNode'
import { EntityNode, type EntityNodeData } from '../../components/graph/EntityNode'
import {
    entityNode,
    expandAsset,
    expandChildren,
    expandEntities,
    mergeGraph,
    nodeFromAsset,
    PLACEHOLDER_ASSET_ICON,
    type ChildrenPage,
    type EntityKind,
    type EntityPage,
    type EntityRef,
    type Graph,
    type GraphEdge,
    type GraphNode,
    type GraphNodeProperty,
} from '../../components/graph/adapter'
import { computeGraphLayout, type Positions } from '../../components/graph/layout'
import { filterClientRoles, groupContactsByIdentity } from '../../lib/asset-contacts'
import type { components } from '../../api/schema'
import { PageHeader } from '../../components/shared/PageHeader'
import { formatAssetWithPath } from '../../lib/assetPath'
import { SECTION_ICONS } from '../../lib/section-icons'

// URL abbreviation for each non-asset kind, used in the `expanded` token grammar
// (`<abbr>@<assetUid>`). Kept short so URLs stay compact and replayable.
const KIND_ABBR: Record<EntityKind, string> = {
    document: 'doc',
    intervention: 'iv',
    expense: 'exp',
    contract: 'ctr',
    contact: 'con',
}
const ABBR_KIND: Record<string, EntityKind> = Object.fromEntries(
    Object.entries(KIND_ABBR).map(([k, v]) => [v, k as EntityKind]),
) as Record<string, EntityKind>
// Every non-asset kind, in toolbar order — a body click on the FOCAL asset
// expands them all (handleExpandAllKinds).
const ALL_KINDS = Object.keys(KIND_ABBR) as EntityKind[]
// Token prefix for the nested intervention→expenses expansion.
const IVEXP = 'ivexp'
// Token family for an entity's OWN neighbor expansion (a body click on a
// document/expense/contract/contact card, loading whatever the adapter can
// resolve for that specific entity — its asset, or a contract's assets +
// stakeholders). Deliberately distinct from KIND_ABBR (asset → kind-page,
// keyed by an asset uid) and IVEXP (intervention → expenses): the uid here is
// the ENTITY's own uid. Interventions keep their existing IVEXP mechanism
// instead of an entry here.
const ENTITY_EXPAND_ABBR: Partial<Record<EntityKind, string>> = {
    document: 'docx',
    expense: 'expx',
    contract: 'ctrx',
    contact: 'conx',
}
const ENTITY_EXPAND_ABBR_KIND: Record<string, EntityKind> = Object.fromEntries(
    Object.entries(ENTITY_EXPAND_ABBR).map(([k, v]) => [v as string, k as EntityKind]),
) as Record<string, EntityKind>

// Soft cap on on-screen nodes (spec performance guardrail). Past it, further
// expansion prompts the user to collapse something first.
const SOFT_CAP = 200
const CHILDREN_PAGE = 25

// pruneExpandedForFocus keeps an `expanded` token list valid across a
// node-click focus change (§2): unlike a search jump, which clears `expanded`
// entirely, a click on an asset already on screen should leave every OTHER
// still-reachable expansion alone. Plain asset-uid tokens are focus-
// independent (they fetch their own body regardless of the current focus,
// same as today) and are always kept. Anchored tokens
// (`<abbr>@<anchorUid>` kind pages, `ivexp@<ivUid>` nested expenses,
// `<entAbbr>@<entityUid>` entity-own-neighbor expansions) are kept only while
// their anchor is itself still reachable — the new focal asset, a kept plain
// asset token, or an entity uid surfaced by another already-kept token (e.g.
// an intervention pulled in via `iv@<assetUid>` is itself the anchor for its
// own `ivexp@<ivUid>` token). Reachability is resolved as a small fixed-point
// pass using the already-fetched `kindCache` (for `<abbr>@` kind-page tokens)
// AND `entityNeighborsCache` (for `<entAbbr>@` entity-own-neighbor tokens —
// the same source the render path uses to resolve them, see the
// `expandedEntityNeighbors` merge loop below), so it never triggers a fetch
// of its own. Anything whose anchor never becomes reachable is dropped — this
// is what keeps the URL from growing unbounded across repeated re-focusing.
export function pruneExpandedForFocus(
    nextFocusAssetUid: string,
    expandedArr: string[],
    kindCache: Map<string, EntityPage>,
    entityNeighborsCache: Map<string, EntityNeighbors>,
): string[] {
    const plainAssets: string[] = []
    const anchored: Array<{ token: string; prefix: string; anchor: string }> = []
    for (const tok of expandedArr) {
        const at = tok.indexOf('@')
        if (at < 0) {
            plainAssets.push(tok)
            continue
        }
        anchored.push({ token: tok, prefix: tok.slice(0, at), anchor: tok.slice(at + 1) })
    }
    const reachable = new Set<string>([nextFocusAssetUid, ...plainAssets])
    const kept = new Set<string>()
    let changed = true
    while (changed) {
        changed = false
        for (const a of anchored) {
            if (kept.has(a.token) || !reachable.has(a.anchor)) continue
            kept.add(a.token)
            changed = true
            // A kind-page token's own items become reachable anchors too, so
            // a downstream token depending on one of them (e.g. an
            // intervention's `ivexp@`) is evaluated on the next pass.
            const kind = ABBR_KIND[a.prefix]
            if (kind) {
                const page = kindCache.get(`${kind}:${a.anchor}`)
                for (const item of page?.items ?? []) reachable.add(item.uid)
            }
            // An entity-own-neighbor token (`docx@`/`expx@`/`ctrx@`/`conx@`)
            // resolves to asset uid(s) — and, for a contract, its derived
            // stakeholder contacts — via entityNeighborsCache. Those resolved
            // uids become reachable anchors too, so a downstream token
            // depending on one of them (e.g. a `doc@<assetUid>` kind page on
            // an asset only surfaced through a contract's own-neighbor
            // expansion) is kept instead of being dropped even though its
            // target is still present in the recomputed graph.
            const entKind = ENTITY_EXPAND_ABBR_KIND[a.prefix]
            if (entKind) {
                const neighbors = entityNeighborsCache.get(`${entKind}:${a.anchor}`)
                for (const au of neighbors?.assetUids ?? []) reachable.add(au)
                for (const c of neighbors?.contacts ?? []) reachable.add(c.uid)
            }
        }
    }
    return [...plainAssets, ...anchored.filter((a) => kept.has(a.token)).map((a) => a.token)]
}

// Property codes requested for node enrichment (spec "Node design" — up to ~3
// property values per node). The ontology has no asset-type "primary property"
// flag (checked docs/ontology/property-definitions.md and the PropertyDefinition
// schema — no such field), so this is a deliberately generic, asset-type-agnostic
// fallback: the surface-area family already used as the canonical "headline
// surface" in front/src/lib/asset-header-slots.ts (covers land/building/unit
// assets) plus the equipment trio used across the equipment library docs
// (condition/brand). Codes with no value for a given asset
// simply don't appear — AssetNode only renders whatever comes back, capped at 3.
const GRAPH_PROPERTY_CODES = [
    'surface_area',
    'usable_area',
    'living_area',
    'gross_floor_area',
    'equipment_condition',
    'equipment_brand',
]

type AssetRelationsBlock = components['schemas']['AssetRelationsBlock']
type Contract = components['schemas']['Contract']

// Result of resolving a single entity's own neighbors (§1 resolvable-neighbor
// expansion, keyed by ENTITY_EXPAND_ABBR): the asset(s) it is linked to, and —
// contracts only — its derived stakeholder contacts.
export interface EntityNeighbors {
    assetUids: string[]
    contacts?: EntityRef[]
}

// Shape of one row from GET /assets/{uid}/properties (AssetPropertyValueListResponse).
interface AssetPropertyValueResponse {
    value?: unknown
    property_definition?: { code?: string }
}

// toOwnProperties picks up to 3 non-empty values from a /properties response,
// resolving each row's code via the embedded property_definition (the endpoint
// doesn't return a bare `code` field — see front/CLAUDE.md "Embedded Objects").
function toOwnProperties(rows: AssetPropertyValueResponse[], max = 3): GraphNodeProperty[] {
    const out: GraphNodeProperty[] = []
    for (const row of rows) {
        const code = row.property_definition?.code
        if (!code || row.value == null || row.value === '') continue
        const value = typeof row.value === 'object' ? JSON.stringify(row.value) : String(row.value)
        out.push({ code, value })
        if (out.length >= max) break
    }
    return out
}

// Contacts have no dedicated list endpoint — they are derived client-side from
// the asset's contracts→stakeholders via the SAME shared grouping module the
// Contacts tab (components/assets/asset-contacts.tsx) uses, so both surfaces
// agree on contact identity and CLIENT-role hiding by construction rather than
// by two independently-maintained copies. Groups (party_group users, e.g. an
// indivision) surface as one contact node keyed by the group uid; the
// CLIENT-only stakeholder role is filtered out (showClientRoles=false), same
// default as the Contacts tab.
function deriveContacts(contracts: Contract[]): EntityRef[] {
    const items = filterClientRoles(groupContactsByIdentity(contracts), false)
    return items
        .map((item) =>
            item.kind === 'group'
                ? { uid: item.groupUid, name: item.groupName || item.groupUid }
                : item.uid
                  ? { uid: item.uid, name: item.name || item.uid }
                  : undefined,
        )
        .filter((ref): ref is EntityRef => ref !== undefined)
}

// Money is stored in integer minor units (cents) — mirror InterventionsPage's
// formatCost for expense node labels lacking an explicit label.
function formatAmount(amount?: number | null, currency?: string | null): string {
    if (amount == null) return ''
    // No euro fallback — see InterventionsPage.formatCost.
    return currency
        ? new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100)
        : (amount / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

interface GraphSearch {
    focus?: string
    expanded?: string
}

export const Route = createFileRoute('/$org/graph')({
    component: GraphExplorer,
    validateSearch: (search: Record<string, unknown>): GraphSearch => ({
        focus: typeof search.focus === 'string' ? search.focus : undefined,
        expanded: typeof search.expanded === 'string' ? search.expanded : undefined,
    }),
})

const nodeTypes: NodeTypes = { asset: AssetNode, entity: EntityNode }

// Fallback footprint used only for the rare frame where the focal card's DOM
// element can't be found yet (see measureFocalFootprint below). Mirrors each
// card type's actual CSS sizing — AssetNode is `w-[180px]` with a compact
// (no-picture, no-property) height around 130px; EntityNode is `w-[150px]`
// and always a single ~48px row — rather than forceLayout's NODE_WIDTH/
// NODE_HEIGHT, which that file itself documents as collision-spacing padding
// ("slightly larger than the rendered card"), not real card geometry.
const ASSET_CARD_FALLBACK = { width: 180, height: 130 }
const ENTITY_CARD_FALLBACK = { width: 150, height: 48 }

// focalCardSelector locates the focal card's own rendered element by its
// existing data-testid — `graph-node-<assetUid>` for an AssetNode, or
// `graph-entity-<kind>-<uid>` for an EntityNode (focalNodeId is `<uid>` or
// `<kind>:<uid>` respectively, see focalNodeId's derivation above).
function focalCardSelector(focalNodeId: string): string {
    const at = focalNodeId.indexOf(':')
    return at > 0
        ? `[data-testid="graph-entity-${CSS.escape(focalNodeId.slice(0, at))}-${CSS.escape(focalNodeId.slice(at + 1))}"]`
        : `[data-testid="graph-node-${CSS.escape(focalNodeId)}"]`
}

// measureFocalFootprint reads the focal node's REAL on-screen size from the
// DOM — its rendered card, not a layout-spacing constant — so centering lands
// on the card's true midpoint regardless of how tall/wide it happens to be
// (a property-rich AssetNode, or the much smaller EntityNode). React Flow's
// own per-node `measured` dimensions were tried first (see git history) but
// stay empty in this app's setup — nodes use a controlled `position` prop and
// measuring never latches — so this queries the rendered card element
// directly via its existing data-testid and converts its screen-space
// bounding box back to flow-space by dividing out the current zoom (React
// Flow scales the whole canvas with a CSS transform, so a card's on-screen
// pixel size is its flow-space size × zoom).
function measureFocalFootprint(
    container: HTMLElement | null,
    focalNodeId: string,
    zoom: number,
): { width: number; height: number } | undefined {
    if (!container) return undefined
    const el = container.querySelector<HTMLElement>(focalCardSelector(focalNodeId))
    if (!el) return undefined
    const rect = el.getBoundingClientRect()
    if (!rect.width || !rect.height) return undefined
    return { width: rect.width / zoom, height: rect.height / zoom }
}

// Inner canvas — must live under a ReactFlowProvider so it can call fitView /
// setCenter. Centering is focal-anchored, not a blanket re-fit of the whole
// graph: it only re-runs when the focal node id changes (a node click or a
// search jump) or the Re-layout control fires (relayoutToken bump). A bare
// node-set change (an expansion/collapse under the SAME focus) must never
// move the viewport — new nodes appear around the focal node while it holds
// its screen position — and neither must a user's manual pan/zoom.
function GraphCanvas({
    nodes,
    edges,
    focalNodeId,
    focalContentReady,
    relayoutToken,
    onNodeClick,
    onNodesChange,
    onNodeDragStop,
    onRelayout,
}: {
    nodes: RFNode[]
    edges: RFEdge[]
    focalNodeId?: string
    // Whether the focal node's OWN async content (its /properties fetch, for
    // an AssetNode) has resolved — see the comment on the centering effect
    // below for why this matters for measuring its real footprint.
    focalContentReady: boolean
    relayoutToken: number
    onNodeClick: (node: RFNode) => void
    onNodesChange: (changes: NodeChange[]) => void
    onNodeDragStop: (id: string, pos: { x: number; y: number }) => void
    onRelayout: () => void
}) {
    const { t } = useTranslation('graph')
    const { fitView, setCenter, getZoom } = useReactFlow()
    // Real DOM container for the canvas, so the centering effect can look up
    // the focal card's actual rendered element (measureFocalFootprint) rather
    // than a layout-spacing constant.
    const containerRef = useRef<HTMLDivElement>(null)
    // The focal node's OWN position, as currently rendered. Layout for a
    // freshly-focal node is computed by a SEPARATE effect one commit up (in
    // GraphExplorer), so the very first render after a focus change still
    // carries the focal node's placeholder/previous position — depending on
    // just (focalNodeId, relayoutToken) would center on that stale spot and
    // never retry (same id, same token). Keying on the position VALUE itself
    // makes this effect re-run again once the real layout position lands,
    // and settle (no more re-centering) once it stops changing.
    const focalPos = focalNodeId ? nodes.find((n) => n.id === focalNodeId)?.position : undefined
    // Tracks the (focalNodeId, relayoutToken, x, y, focalContentReady) key we
    // last centered on, so an unrelated node-set change (same focus, same
    // relayout generation, focal position and content-readiness unchanged) is
    // a no-op — new nodes appear around the focal node while it holds its
    // screen position. focalContentReady is part of the key (not the gate
    // condition itself) so this effect fires once EARLY, with whatever
    // footprint is measurable yet (typically the fallback, since the card's
    // property row hasn't rendered), and fires again once the focal asset's
    // own fetch resolves and its real (possibly taller) footprint can be
    // measured — an expansion of any OTHER node never touches
    // focalContentReady, so it never re-triggers this second pass.
    const centeredKeyRef = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (nodes.length === 0) return
        if (!focalNodeId) {
            // No focal node to anchor on (shouldn't normally happen — the
            // canvas only mounts once a focus is set) — fall back to framing
            // whatever is on screen, once per relayout generation.
            const key = `none:${relayoutToken}`
            if (centeredKeyRef.current === key) return
            centeredKeyRef.current = key
            void fitView({ padding: 0.3, duration: 250, maxZoom: 1 })
            return
        }
        if (!focalPos) return // focal node not in the node list yet — retry next render
        const key = `${focalNodeId}:${relayoutToken}:${focalPos.x}:${focalPos.y}:${focalContentReady}`
        const isNewKey = centeredKeyRef.current !== key
        if (isNewKey) centeredKeyRef.current = key
        // Center on the focal node's midpoint, measuring the card's ACTUAL
        // rendered footprint (its true width/height, which varies with
        // content — properties, picture, kind-toggle row — for AssetNode, or
        // the fixed small EntityNode size) instead of a fixed layout-spacing
        // constant, so the true visual midpoint lands at the viewport center.
        // The last footprint we centered on. The observer skips the write
        // when the card's measured size has not moved, so a settled card
        // stops feeding setCenter and can no longer re-trigger the observer.
        let lastFootprint: { width: number; height: number } | undefined
        const recenter = () => {
            // Preserve whatever zoom the user is already at — a fitView
            // scoped to one small card would zoom in far too tight — only
            // flooring it so a degenerate near-zero zoom never carries over.
            const zoom = Math.max(getZoom(), 0.6)
            // Falls back to a card-shaped (not collision-shaped) constant
            // only for the rare frame where the DOM element isn't found yet.
            const footprint =
                measureFocalFootprint(containerRef.current, focalNodeId, zoom) ??
                (focalNodeId.includes(':') ? ENTITY_CARD_FALLBACK : ASSET_CARD_FALLBACK)
            if (
                lastFootprint &&
                footprint.width === lastFootprint.width &&
                footprint.height === lastFootprint.height
            )
                return
            lastFootprint = footprint
            void setCenter(focalPos.x + footprint.width / 2, focalPos.y + footprint.height / 2, { zoom, duration: 250 })
        }
        // Only fire the initial centering once per key — an unrelated
        // node-set change under the same focus must never re-fit.
        if (isNewKey) recenter()
        // The card's real footprint can keep growing for a couple of frames
        // after its first paint (e.g. a web-font swap reflowing the
        // properties text, or the picture thumbnail loading) — a single
        // measurement taken right on mount can undershoot the card's settled
        // size. A ResizeObserver on the card's OWN element catches that
        // settling and re-centers once more, without re-firing on pan/zoom
        // (those are CSS transforms on an ancestor, which ResizeObserver
        // ignores) or on sibling node changes (it only watches the focal
        // card's own box, which an expansion elsewhere never touches).
        // Attached unconditionally (not gated on isNewKey) so it survives
        // React StrictMode's dev-only double-invoke of this effect: the
        // synthetic first cleanup disconnects the first observer before any
        // real resize could fire, and since centeredKeyRef already matches
        // on the second invocation, isNewKey is false there — but the
        // observer itself must still be (re)created so a persisting one
        // exists to catch the card's later settling.
        const el = containerRef.current?.querySelector(focalCardSelector(focalNodeId))
        if (!el) return
        // Defer the re-center to the next animation frame instead of writing
        // setCenter straight from the delivery callback. A synchronous
        // observe → measure → setCenter chain runs inside the observer's own
        // callback and overruns the browser's per-frame notification budget,
        // which raises the benign "ResizeObserver loop completed with
        // undelivered notifications" error. rAF moves the write out of the
        // callback and coalesces a settling burst into one recenter per frame.
        let rafId = 0
        const ro = new ResizeObserver(() => {
            if (rafId) return
            rafId = requestAnimationFrame(() => {
                rafId = 0
                recenter()
            })
        })
        ro.observe(el)
        return () => {
            ro.disconnect()
            if (rafId) cancelAnimationFrame(rafId)
        }
    }, [nodes.length, focalNodeId, focalPos, focalContentReady, relayoutToken, fitView, setCenter, getZoom])
    return (
        <ReactFlow
            ref={containerRef}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onNodeClick={(_e, node) => onNodeClick(node)}
            onNodeDragStop={(_e, node) =>
                onNodeDragStop(node.id, { x: node.position.x, y: node.position.y })
            }
            minZoom={0.1}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
        >
            <Background />
            <Controls>
                <ControlButton
                    onClick={onRelayout}
                    title={t('controls.relayout')}
                    aria-label={t('controls.relayout')}
                    data-testid="graph-relayout"
                >
                    <AppIcon path={mdiAutoFix} size="sm" />
                </ControlButton>
            </Controls>
        </ReactFlow>
    )
}

// A minimal asset shape the adapter tolerates. We fetch full bodies but only
// depend on a subset.
type AssetBody = Parameters<typeof expandAsset>[0]

function GraphExplorer() {
    const { t } = useTranslation('graph')
    const { org } = useParams({ from: '/$org/graph' })
    const search = useSearch({ from: '/$org/graph' })
    const navigate = useNavigate({ from: '/$org/graph' })
    const { request, apiBaseUrl } = useApi()

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('title') },
    ])

    // `focus` is the raw URL token, kept verbatim for every URL write. An asset
    // focus is a plain uid; a non-asset focus is kind-qualified (`doc@<uid>`,
    // `ctr@<uid>`, `exp@<uid>`, `iv@<uid>`) so the graph can center on a
    // document / contract / expense / intervention (§5). focalNodeId is the id of
    // the focal graph node — the asset uid, or `<kind>:<uid>` for a non-asset.
    const focus = search.focus
    const { focusKind, focusUid, focalNodeId } = useMemo(() => {
        if (!focus) return { focusKind: 'asset' as const, focusUid: undefined, focalNodeId: undefined }
        const at = focus.indexOf('@')
        if (at > 0) {
            const kind = ABBR_KIND[focus.slice(0, at)]
            if (kind) {
                const uid = focus.slice(at + 1)
                return { focusKind: kind, focusUid: uid, focalNodeId: `${kind}:${uid}` }
            }
        }
        return { focusKind: 'asset' as const, focusUid: focus, focalNodeId: focus }
    }, [focus])
    const expandedArr = useMemo(
        () => (search.expanded ? search.expanded.split(',').filter(Boolean) : []),
        [search.expanded],
    )
    const locale = (typeof navigator !== 'undefined' && navigator.language?.startsWith('fr')) ? 'fr' : 'en'

    // Asset-type lookups for the adapter (a per-asset emoji wins over these).
    // Stabilize the array reference: useAssetTypes returns a fresh [] on every
    // render while loading, which would otherwise churn every memo below it and
    // keep React Flow from settling (nodes stuck at visibility:hidden).
    const assetTypesQuery = useAssetTypes(org)
    const assetTypes = useMemo(() => assetTypesQuery.data ?? [], [assetTypesQuery.data])
    const typeByCode = useCallback(
        (code: string | undefined) =>
            code ? assetTypes.find((at) => at.code === code) : undefined,
        [assetTypes],
    )
    const typeMetaByUid = useCallback(
        (uid: string | null | undefined) => {
            if (!uid) return undefined
            const at = assetTypes.find((t) => t.uid === uid)
            return at ? { code: at.code, emoji: at.emoji ?? undefined } : undefined
        },
        [assetTypes],
    )
    const adapterCtx = useMemo(
        () => ({ assetTypeByCode: typeByCode, assetTypeMetaByUid: typeMetaByUid }),
        [typeByCode, typeMetaByUid],
    )

    // Fetch caches, held in state so the graph derivation (below) can read them
    // during render and recompute when a fetch settles. inflightRef only guards
    // against duplicate concurrent fetches and is never read during render.
    const [assetCache, setAssetCache] = useState<Map<string, AssetBody>>(new Map())
    const [childrenCache, setChildrenCache] = useState<Map<string, ChildrenPage>>(new Map())
    // relationsCache holds the include_properties-enriched relations block per
    // expanded uid (separate from the plain-relations block embedded on the
    // asset body, which never carries property values). ownPropertiesCache
    // holds each expanded asset's own up-to-3 property rows, resolved via a
    // dedicated /properties call since the single-asset GET has no
    // include=properties support (see GRAPH_PROPERTY_CODES comment above).
    const [relationsCache, setRelationsCache] = useState<Map<string, AssetRelationsBlock>>(new Map())
    const [ownPropertiesCache, setOwnPropertiesCache] = useState<Map<string, GraphNodeProperty[]>>(new Map())
    // Non-asset kind pages (§4), keyed by `${kind}:${anchorId}`. Anchor is an
    // asset uid for asset-level kinds, or `intervention:<uid>` for the nested
    // intervention→expenses expansion.
    const [kindCache, setKindCache] = useState<Map<string, EntityPage>>(new Map())
    // Entity-own-neighbor resolutions (§1), keyed by `${kind}:${entityUid}` —
    // the same id shape as the graph node itself.
    const [entityNeighborsCache, setEntityNeighborsCache] = useState<Map<string, EntityNeighbors>>(new Map())
    const inflightRef = useRef<Set<string>>(new Set())
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
    const [capOpen, setCapOpen] = useState(false)
    // Resolved focal non-asset entity (§5): its display name and the asset(s) it
    // is linked to, so the graph can render the entity as the focal node with an
    // edge to each asset the user can then expand into the hierarchy.
    const [focusEntity, setFocusEntity] = useState<
        { kind: EntityKind; uid: string; name: string; assetUids: string[] } | null
    >(null)

    // Parse the `expanded` tokens into their four flavours: plain asset uids,
    // `<abbr>@<assetUid>` kind expansions, `ivexp@<ivUid>` nested expense
    // expansions, and `<entAbbr>@<entityUid>` entity-own-neighbor expansions
    // (§1). Keeps the URL the single source of truth (fully replayable).
    const { expandedAssets, expandedKindMap, expandedIvExpenses, expandedEntityNeighbors } = useMemo(() => {
        const assets: string[] = []
        const kinds = new Map<string, Set<EntityKind>>()
        const iv = new Set<string>()
        const entities = new Map<EntityKind, Set<string>>()
        for (const tok of expandedArr) {
            const at = tok.indexOf('@')
            if (at < 0) {
                assets.push(tok)
                continue
            }
            const prefix = tok.slice(0, at)
            const id = tok.slice(at + 1)
            if (prefix === IVEXP) {
                iv.add(id)
            } else if (ABBR_KIND[prefix]) {
                const set = kinds.get(id) ?? new Set<EntityKind>()
                set.add(ABBR_KIND[prefix])
                kinds.set(id, set)
            } else if (ENTITY_EXPAND_ABBR_KIND[prefix]) {
                const kind = ENTITY_EXPAND_ABBR_KIND[prefix]
                const set = entities.get(kind) ?? new Set<string>()
                set.add(id)
                entities.set(kind, set)
            }
        }
        return { expandedAssets: assets, expandedKindMap: kinds, expandedIvExpenses: iv, expandedEntityNeighbors: entities }
    }, [expandedArr])

    // effectiveExpanded = the focal ASSET (always) ∪ the user's explicit asset
    // expansions. A non-asset focus is not an asset expansion.
    const effectiveExpanded = useMemo(() => {
        const s = new Set<string>(expandedAssets)
        if (focusKind === 'asset' && focusUid) s.add(focusUid)
        return s
    }, [expandedAssets, focusKind, focusUid])

    // Fetch a node's neighbors: its own body (parent), its first children page,
    // its relations enriched with neighbor property values, and its own
    // property rows. All four requests fire concurrently and their cache
    // updates are committed together right after — not after each individual
    // `await` — so React batches them into a single re-render. Committing
    // them across four separate microtask ticks instead (one setState per
    // await) was measured to make React Flow re-measure/hide nodes on every
    // intermediate update, which could leave nodes stuck invisible for
    // several seconds under load.
    const fetchNode = useCallback(
        async (uid: string) => {
            if (inflightRef.current.has(uid)) return
            inflightRef.current.add(uid)
            setLoadingIds((prev) => new Set(prev).add(uid))
            try {
                const [bodyRes, childrenRes, relationsRes, propertiesRes] = await Promise.allSettled([
                    request(
                        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
                        'GET',
                        { params: { path: { orgCode: org, assetUid: uid }, query: { include: ['picture', 'link_counts'] } } },
                    ),
                    request(
                        '/api/v1/organizations/{orgCode}/assets',
                        'GET',
                        {
                            params: {
                                path: { orgCode: org },
                                // include=properties embeds each child's current
                                // property values inline (list endpoint only — see
                                // GRAPH_PROPERTY_CODES comment) at no extra request.
                                query: { parent_asset_uid: uid, limit: CHILDREN_PAGE, offset: 0, include: ['properties', 'link_counts'] },
                            },
                        },
                    ),
                    // Relations enriched with property values on the neighbor
                    // refs (spec "Node enrichment": include_properties=…).
                    // Separate from the asset body above because the asset GET
                    // embeds relations unconditionally but never with
                    // include_properties.
                    request(
                        '/api/v1/organizations/{orgCode}/assets/{assetUid}/relations',
                        'GET',
                        {
                            params: {
                                path: { orgCode: org, assetUid: uid },
                                query: { include_properties: GRAPH_PROPERTY_CODES.join(',') },
                            },
                        },
                    ),
                    // The node's own property rows — the single-asset GET has
                    // no include=properties, so resolve them via the dedicated
                    // properties endpoint.
                    request(
                        '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties',
                        'GET',
                        { params: { path: { orgCode: org, assetUid: uid } } },
                    ),
                ])

                if (bodyRes.status === 'fulfilled') {
                    const body = bodyRes.value as AssetBody
                    setAssetCache((prev) => new Map(prev).set(uid, body))
                }
                if (childrenRes.status === 'fulfilled') {
                    const page = childrenRes.value as {
                        data?: AssetBody[]
                        total?: number
                        limit?: number
                        offset?: number
                    }
                    setChildrenCache((prev) =>
                        new Map(prev).set(uid, {
                            parentUid: uid,
                            items: page.data ?? [],
                            total: page.total ?? (page.data?.length ?? 0),
                            limit: page.limit ?? CHILDREN_PAGE,
                            offset: 0,
                        }),
                    )
                }
                if (relationsRes.status === 'fulfilled') {
                    setRelationsCache((prev) => new Map(prev).set(uid, relationsRes.value as AssetRelationsBlock))
                }
                // else: non-blocking — edges still render from the asset
                // body's plain (unenriched) relations block as a fallback.
                if (propertiesRes.status === 'fulfilled') {
                    const propsData = (propertiesRes.value as { data?: AssetPropertyValueResponse[] }).data ?? []
                    setOwnPropertiesCache((prev) => new Map(prev).set(uid, toOwnProperties(propsData)))
                }
                // else: non-blocking — the node just renders without property rows.
            } catch {
                // Non-blocking: a failed neighbor fetch just leaves that node a
                // leaf. No toast per repo convention.
            } finally {
                inflightRef.current.delete(uid)
                setLoadingIds((prev) => {
                    const n = new Set(prev)
                    n.delete(uid)
                    return n
                })
            }
        },
        [org, request],
    )

    // Resolve a non-asset focal entity (§5): fetch its detail body, extract its
    // display name and linked asset uid(s). Asset focus clears this.
    useEffect(() => {
        if (focusKind === 'asset' || !focusUid) {
            setFocusEntity(null)
            return
        }
        let cancelled = false
        void (async () => {
            try {
                let name = focusUid
                const assetUids: string[] = []
                if (focusKind === 'document') {
                    const res = (await request('/api/v1/organizations/{orgCode}/documents/{documentUid}', 'GET', {
                        params: { path: { orgCode: org, documentUid: focusUid } },
                    })) as { name?: string; file_name?: string; asset_uid?: string | null }
                    name = res.name || res.file_name || focusUid
                    if (res.asset_uid) assetUids.push(res.asset_uid)
                } else if (focusKind === 'expense') {
                    const res = (await request('/api/v1/organizations/{orgCode}/expenses/{expenseUid}', 'GET', {
                        params: { path: { orgCode: org, expenseUid: focusUid } },
                    })) as { label?: string; amount?: number; currency?: string; asset_uid?: string }
                    name = res.label || formatAmount(res.amount, res.currency) || focusUid
                    if (res.asset_uid) assetUids.push(res.asset_uid)
                } else if (focusKind === 'intervention') {
                    const res = (await request('/api/v1/organizations/{orgCode}/interventions/{interventionUid}', 'GET', {
                        params: { path: { orgCode: org, interventionUid: focusUid } },
                    })) as { title?: string; reference?: string; asset_uid?: string }
                    name = res.title || res.reference || focusUid
                    if (res.asset_uid) assetUids.push(res.asset_uid)
                } else if (focusKind === 'contract') {
                    const [detail, assetsRes] = await Promise.allSettled([
                        request('/api/v1/organizations/{orgCode}/contracts/{contractUid}', 'GET', {
                            params: { path: { orgCode: org, contractUid: focusUid } },
                        }),
                        request('/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets', 'GET', {
                            params: { path: { orgCode: org, contractUid: focusUid } },
                        }),
                    ])
                    if (detail.status === 'fulfilled') {
                        const c = detail.value as { name?: string; contract_type?: { names?: Record<string, string> } }
                        name = c.name || c.contract_type?.names?.[locale] || c.contract_type?.names?.en || focusUid
                    }
                    if (assetsRes.status === 'fulfilled') {
                        const rows = (assetsRes.value as { data?: Array<{ asset_uid?: string }> }).data ?? []
                        for (const r of rows) if (r.asset_uid) assetUids.push(r.asset_uid)
                    }
                }
                if (!cancelled) setFocusEntity({ kind: focusKind, uid: focusUid, name, assetUids })
            } catch {
                if (!cancelled) setFocusEntity({ kind: focusKind, uid: focusUid, name: focusUid, assetUids: [] })
            }
        })()
        return () => {
            cancelled = true
        }
    }, [focusKind, focusUid, org, request, locale])

    // Reconcile fetches whenever the expansion set (or focal entity's assets) change.
    useEffect(() => {
        for (const uid of effectiveExpanded) {
            if (!assetCache.has(uid)) void fetchNode(uid)
        }
        if (focusEntity) {
            for (const au of focusEntity.assetUids) if (!assetCache.has(au)) void fetchNode(au)
        }
    }, [effectiveExpanded, focusEntity, assetCache, fetchNode])

    // Load the next children page for a "+N more" pseudo-node.
    const handleLoadMore = useCallback(
        async (parentUid: string, offset: number) => {
            setLoadingIds((prev) => new Set(prev).add(parentUid))
            try {
                const page = (await request(
                    '/api/v1/organizations/{orgCode}/assets',
                    'GET',
                    {
                        params: {
                            path: { orgCode: org },
                            query: { parent_asset_uid: parentUid, limit: CHILDREN_PAGE, offset, include: ['properties', 'link_counts'] },
                        },
                    },
                )) as { data?: AssetBody[]; total?: number; limit?: number }
                setChildrenCache((prev) => {
                    const existing = prev.get(parentUid)
                    const merged = [...(existing?.items ?? []), ...(page.data ?? [])]
                    return new Map(prev).set(parentUid, {
                        parentUid,
                        items: merged,
                        total: page.total ?? merged.length,
                        limit: CHILDREN_PAGE,
                        offset: 0,
                    })
                })
            } catch {
                /* non-blocking */
            } finally {
                setLoadingIds((prev) => {
                    const n = new Set(prev)
                    n.delete(parentUid)
                    return n
                })
            }
        },
        [org, request],
    )

    // Fetch one non-asset kind for an asset (§4). Each kind uses its own list
    // endpoint; the responses are flattened to {uid, name} EntityRefs. Contacts
    // have no list endpoint and are derived from contracts→stakeholders.
    const fetchKind = useCallback(
        async (assetUid: string, kind: EntityKind, offset = 0) => {
            const cacheKey = `${kind}:${assetUid}`
            const loadKey = `k:${cacheKey}`
            if (inflightRef.current.has(loadKey)) return
            inflightRef.current.add(loadKey)
            setLoadingIds((prev) => new Set(prev).add(assetUid))
            const page = Math.floor(offset / CHILDREN_PAGE) + 1
            try {
                let refs: EntityRef[] = []
                let total = 0
                if (kind === 'document') {
                    const res = (await request('/api/v1/organizations/{orgCode}/documents', 'GET', {
                        params: { path: { orgCode: org }, query: { asset_uid: assetUid, limit: CHILDREN_PAGE, page } },
                    })) as { data?: Array<{ uid: string; name?: string; file_name?: string }>; total?: number }
                    refs = (res.data ?? []).map((d) => ({ uid: d.uid, name: d.name || d.file_name || d.uid }))
                    total = res.total ?? refs.length
                } else if (kind === 'intervention') {
                    const res = (await request('/api/v1/organizations/{orgCode}/interventions', 'GET', {
                        params: { path: { orgCode: org }, query: { asset_uid: assetUid, limit: CHILDREN_PAGE, page } },
                    })) as { data?: Array<{ uid?: string; title?: string; reference?: string }>; total?: number }
                    refs = (res.data ?? [])
                        .filter((i) => i.uid)
                        .map((i) => ({ uid: i.uid as string, name: i.title || i.reference || (i.uid as string) }))
                    total = res.total ?? refs.length
                } else if (kind === 'expense') {
                    const res = (await request('/api/v1/organizations/{orgCode}/expenses', 'GET', {
                        params: { path: { orgCode: org }, query: { asset_uid: assetUid, limit: CHILDREN_PAGE, page } },
                    })) as { data?: Array<{ uid?: string; label?: string; amount?: number; currency?: string; intervention_uid?: string | null }>; total?: number }
                    refs = (res.data ?? [])
                        .filter((e) => e.uid)
                        .map((e) => ({
                            uid: e.uid as string,
                            name: e.label || formatAmount(e.amount, e.currency) || (e.uid as string),
                            interventionUid: e.intervention_uid ?? undefined,
                        }))
                    total = res.total ?? refs.length
                } else if (kind === 'contract') {
                    const res = (await request('/api/v1/organizations/{orgCode}/contracts', 'GET', {
                        params: { path: { orgCode: org }, query: { asset_uid: assetUid, limit: CHILDREN_PAGE, page } },
                    })) as { data?: Contract[]; total?: number }
                    const loc = navigator.language?.startsWith('fr') ? 'fr' : 'en'
                    refs = (res.data ?? [])
                        .filter((c) => c.uid)
                        .map((c) => ({
                            uid: c.uid as string,
                            name:
                                c.name ||
                                c.contract_type?.names?.[loc] ||
                                c.contract_type?.names?.en ||
                                (c.uid as string),
                        }))
                    total = res.total ?? refs.length
                } else {
                    // contact — derived from contracts→stakeholders (no list API),
                    // via the same shared grouping module asset-contacts.tsx uses.
                    const res = (await request('/api/v1/organizations/{orgCode}/contracts', 'GET', {
                        params: {
                            path: { orgCode: org },
                            query: { asset_uid: assetUid, include: 'stakeholders,user,stakeholder_type,members', limit: 50 },
                        },
                    })) as { data?: Contract[] }
                    refs = deriveContacts(res.data ?? [])
                    total = refs.length // client-derived; no server pagination
                }
                setKindCache((prev) => {
                    const existing = prev.get(cacheKey)
                    const items = offset > 0 ? [...(existing?.items ?? []), ...refs] : refs
                    return new Map(prev).set(cacheKey, { anchorId: assetUid, kind, items, total, limit: CHILDREN_PAGE, offset: 0 })
                })
            } catch {
                /* non-blocking */
            } finally {
                inflightRef.current.delete(loadKey)
                setLoadingIds((prev) => {
                    const n = new Set(prev)
                    n.delete(assetUid)
                    return n
                })
            }
        },
        [org, request],
    )

    // Fetch an intervention's expenses (nested expansion, `ivexp@<uid>`). Anchored
    // at the intervention node so the expense edges point back at it.
    const fetchIvExpenses = useCallback(
        async (ivUid: string, offset = 0) => {
            const anchorId = `intervention:${ivUid}`
            const cacheKey = `expense:${anchorId}`
            const loadKey = `k:${cacheKey}`
            if (inflightRef.current.has(loadKey)) return
            inflightRef.current.add(loadKey)
            setLoadingIds((prev) => new Set(prev).add(`ivexp:${ivUid}`))
            const page = Math.floor(offset / CHILDREN_PAGE) + 1
            try {
                const res = (await request('/api/v1/organizations/{orgCode}/expenses', 'GET', {
                    params: { path: { orgCode: org }, query: { intervention_uid: ivUid, limit: CHILDREN_PAGE, page } },
                })) as { data?: Array<{ uid?: string; label?: string; amount?: number; currency?: string }>; total?: number }
                const refs: EntityRef[] = (res.data ?? [])
                    .filter((e) => e.uid)
                    .map((e) => ({ uid: e.uid as string, name: e.label || formatAmount(e.amount, e.currency) || (e.uid as string), interventionUid: ivUid }))
                const total = res.total ?? refs.length
                setKindCache((prev) => {
                    const existing = prev.get(cacheKey)
                    const items = offset > 0 ? [...(existing?.items ?? []), ...refs] : refs
                    return new Map(prev).set(cacheKey, { anchorId, kind: 'expense', items, total, limit: CHILDREN_PAGE, offset: 0 })
                })
            } catch {
                /* non-blocking */
            } finally {
                inflightRef.current.delete(loadKey)
                setLoadingIds((prev) => {
                    const n = new Set(prev)
                    n.delete(`ivexp:${ivUid}`)
                    return n
                })
            }
        },
        [org, request],
    )

    // Resolve a single entity's own neighbors (§1 resolvable-neighbor
    // expansion, `<entAbbr>@<entityUid>`): document/expense → their asset;
    // contract → its assets + derived stakeholder contacts; contact → every
    // asset it is attached to (via the `user_uid` contract filter — a
    // party_group is itself a user record, so this covers group contacts
    // too). Mirrors fetchIvExpenses's shape (on-demand, cached, non-blocking).
    const fetchEntityNeighbors = useCallback(
        async (kind: EntityKind, uid: string) => {
            const cacheKey = `${kind}:${uid}`
            const loadKey = `en:${cacheKey}`
            if (inflightRef.current.has(loadKey)) return
            inflightRef.current.add(loadKey)
            setLoadingIds((prev) => new Set(prev).add(`en:${kind}:${uid}`))
            try {
                let assetUids: string[] = []
                let contacts: EntityRef[] | undefined
                if (kind === 'document') {
                    const res = (await request('/api/v1/organizations/{orgCode}/documents/{documentUid}', 'GET', {
                        params: { path: { orgCode: org, documentUid: uid } },
                    })) as { asset_uid?: string | null }
                    if (res.asset_uid) assetUids = [res.asset_uid]
                } else if (kind === 'expense') {
                    const res = (await request('/api/v1/organizations/{orgCode}/expenses/{expenseUid}', 'GET', {
                        params: { path: { orgCode: org, expenseUid: uid } },
                    })) as { asset_uid?: string }
                    if (res.asset_uid) assetUids = [res.asset_uid]
                } else if (kind === 'contract') {
                    const res = (await request('/api/v1/organizations/{orgCode}/contracts/{contractUid}', 'GET', {
                        params: {
                            path: { orgCode: org, contractUid: uid },
                            query: { include: 'assets,stakeholders,user,stakeholder_type,members' },
                        },
                    })) as Contract & { assets?: Array<{ asset_uid?: string }> }
                    assetUids = (res.assets ?? []).map((a) => a.asset_uid).filter((u): u is string => !!u)
                    contacts = deriveContacts([res])
                } else if (kind === 'contact') {
                    const res = (await request('/api/v1/organizations/{orgCode}/contracts', 'GET', {
                        params: {
                            path: { orgCode: org },
                            query: { user_uid: uid, include: 'assets', limit: 100 },
                        },
                    })) as { data?: Array<{ assets?: Array<{ asset_uid?: string }> }> }
                    const seen = new Set<string>()
                    for (const c of res.data ?? []) {
                        for (const a of c.assets ?? []) if (a.asset_uid) seen.add(a.asset_uid)
                    }
                    assetUids = [...seen]
                }
                setEntityNeighborsCache((prev) => new Map(prev).set(cacheKey, { assetUids, contacts }))
            } catch {
                /* non-blocking — the card just stays without neighbors. */
            } finally {
                inflightRef.current.delete(loadKey)
                setLoadingIds((prev) => {
                    const n = new Set(prev)
                    n.delete(`en:${kind}:${uid}`)
                    return n
                })
            }
        },
        [org, request],
    )

    // Reconcile kind fetches whenever the kind-expansion set changes.
    useEffect(() => {
        for (const [assetUid, kinds] of expandedKindMap) {
            for (const kind of kinds) {
                if (!kindCache.has(`${kind}:${assetUid}`)) void fetchKind(assetUid, kind)
            }
        }
        for (const ivUid of expandedIvExpenses) {
            if (!kindCache.has(`expense:intervention:${ivUid}`)) void fetchIvExpenses(ivUid)
        }
    }, [expandedKindMap, expandedIvExpenses, kindCache, fetchKind, fetchIvExpenses])

    // Reconcile entity-own-neighbor fetches whenever that expansion set changes.
    useEffect(() => {
        for (const [kind, uids] of expandedEntityNeighbors) {
            for (const uid of uids) {
                if (!entityNeighborsCache.has(`${kind}:${uid}`)) void fetchEntityNeighbors(kind, uid)
            }
        }
    }, [expandedEntityNeighbors, entityNeighborsCache, fetchEntityNeighbors])

    const handleLoadMoreKind = useCallback(
        (anchorId: string, kind: EntityKind, offset: number) => {
            if (anchorId.startsWith('intervention:')) {
                void fetchIvExpenses(anchorId.slice('intervention:'.length), offset)
            } else {
                void fetchKind(anchorId, kind, offset)
            }
        },
        [fetchKind, fetchIvExpenses],
    )

    // Derive the graph purely from (focus, expanded, caches). This makes the URL
    // fully replayable: same focus + expanded ⇒ same node/edge set.
    const graph: Graph = useMemo(() => {
        let g: Graph = { nodes: [], edges: [] }
        if (focusKind === 'asset' && focusUid) {
            g = mergeGraph(g, {
                nodes: [
                    {
                        id: focusUid,
                        assetUid: focusUid,
                        name: focusUid,
                        icon: PLACEHOLDER_ASSET_ICON,
                        properties: [],
                        kind: 'asset',
                    },
                ],
                edges: [],
            })
        }
        // Non-asset focal entity (§5): the entity node + an edge to each linked
        // asset (rendered from its cached body when available, else a placeholder
        // the user can expand).
        if (focusEntity) {
            const entId = `${focusEntity.kind}:${focusEntity.uid}`
            g = mergeGraph(g, {
                nodes: [entityNode(focusEntity.kind, { uid: focusEntity.uid, name: focusEntity.name })],
                edges: [],
            })
            for (const au of focusEntity.assetUids) {
                const body = assetCache.get(au)
                const assetNode = body
                    ? nodeFromAsset(body, adapterCtx)
                    : { id: au, assetUid: au, name: au, icon: PLACEHOLDER_ASSET_ICON, properties: [], kind: 'asset' as const }
                g = mergeGraph(g, {
                    nodes: [assetNode],
                    edges: [{ id: `focus:${entId}->${au}`, source: entId, target: au, category: 'relation' }],
                })
            }
        }
        for (const uid of effectiveExpanded) {
            const body = assetCache.get(uid)
            if (body) {
                g = mergeGraph(
                    g,
                    expandAsset(body, {
                        locale,
                        ctx: adapterCtx,
                        relationsBlock: relationsCache.get(uid),
                        ownProperties: ownPropertiesCache.get(uid),
                    }),
                )
            }
            const page = childrenCache.get(uid)
            if (page) g = mergeGraph(g, expandChildren(page, adapterCtx))
        }
        // Non-asset kinds (§4): merge every expanded kind page + nested
        // intervention→expenses page.
        for (const [assetUid, kinds] of expandedKindMap) {
            for (const kind of kinds) {
                const kp = kindCache.get(`${kind}:${assetUid}`)
                if (kp) g = mergeGraph(g, expandEntities(kp))
            }
        }
        for (const ivUid of expandedIvExpenses) {
            const kp = kindCache.get(`expense:intervention:${ivUid}`)
            if (kp) g = mergeGraph(g, expandEntities(kp))
        }
        // Entity-own-neighbor expansions (§1): each expanded document/expense/
        // contract/contact adds its resolved asset(s) — and, for a contract,
        // its derived stakeholder contacts — as nodes edged off the entity.
        for (const [kind, uids] of expandedEntityNeighbors) {
            for (const uid of uids) {
                const en = entityNeighborsCache.get(`${kind}:${uid}`)
                if (!en) continue
                const entId = `${kind}:${uid}`
                const assetNodes: GraphNode[] = en.assetUids.map((au) => {
                    const body = assetCache.get(au)
                    return body
                        ? nodeFromAsset(body, adapterCtx)
                        : { id: au, assetUid: au, name: au, icon: PLACEHOLDER_ASSET_ICON, properties: [], kind: 'asset' as const }
                })
                const assetEdges: GraphEdge[] = en.assetUids.map((au) => ({
                    id: `en:${entId}->${au}`,
                    source: entId,
                    target: au,
                    category: 'relation',
                }))
                g = mergeGraph(g, { nodes: assetNodes, edges: assetEdges })
                if (en.contacts?.length) {
                    const contactNodes = en.contacts.map((c) => entityNode('contact', c))
                    const contactEdges: GraphEdge[] = en.contacts.map((c) => ({
                        id: `enc:${entId}->contact:${c.uid}`,
                        source: entId,
                        target: `contact:${c.uid}`,
                        category: 'relation',
                    }))
                    g = mergeGraph(g, { nodes: contactNodes, edges: contactEdges })
                }
            }
        }
        // Cross-kind edge: an expense edges back to its intervention when both
        // nodes are present (spec §4 — expenses link to interventions).
        const present = new Set(g.nodes.map((n) => n.id))
        const extra: GraphEdge[] = []
        for (const n of g.nodes) {
            if (n.kind !== 'expense' || !n.interventionUid) continue
            const ivId = `intervention:${n.interventionUid}`
            if (present.has(ivId)) {
                extra.push({ id: `x:${n.id}->${ivId}`, source: n.id, target: ivId, category: 'relation' })
            }
        }
        if (extra.length) g = mergeGraph(g, { nodes: [], edges: extra })
        return g
    }, [focusKind, focusUid, focusEntity, effectiveExpanded, expandedKindMap, expandedIvExpenses, expandedEntityNeighbors, locale, adapterCtx, assetCache, childrenCache, relationsCache, ownPropertiesCache, kindCache, entityNeighborsCache])

    const expandedSet = useMemo(() => new Set(expandedAssets), [expandedAssets])

    // Whether the focal node's OWN async content has resolved, for
    // GraphCanvas's centering effect (§3 centering bug fix): an AssetNode's
    // real footprint depends on its /properties fetch (fetchNode), which
    // lands well after the node first appears with a placeholder body — so
    // centering once at first mount would measure the pre-properties
    // (shorter) card and never correct itself. A non-asset focus has a fixed
    // EntityNode footprint and is always "ready". assetCache is populated by
    // fetchNode in one batched commit alongside ownPropertiesCache (see the
    // fetchNode comment above), so its presence is a reliable single signal
    // that the card's final content — including the properties row — is
    // already in this same render, unlike loadingIds, which also toggles for
    // unrelated per-kind fetches (e.g. expanding the focal asset's own
    // documents/expenses) and would cause spurious re-centering on those.
    const focalContentReady = focusKind !== 'asset' || !focusUid || assetCache.has(focusUid)

    // URL mutators.
    // Search-triggered focus: jumping to an unrelated asset from the search
    // box, so any prior expansion state is irrelevant — reset it.
    const setFocusFromSearch = useCallback(
        (uid: string) => {
            navigate({ search: { focus: uid, expanded: undefined } })
        },
        [navigate],
    )
    // Node-click-triggered focus (§2): the clicked asset is already on
    // screen, so its uid and every currently-expanded token stay valid — only
    // tokens anchored on something that falls out of reach of the NEW focus
    // are dropped, so the URL cannot grow unbounded across repeated re-focusing.
    const setFocusFromNodeClick = useCallback(
        (uid: string) => {
            const pruned = pruneExpandedForFocus(uid, expandedArr, kindCache, entityNeighborsCache)
            navigate({ search: { focus: uid, expanded: pruned.length ? pruned.join(',') : undefined } })
        },
        [expandedArr, kindCache, entityNeighborsCache, navigate],
    )
    const openAsset = useCallback(
        (uid: string) => {
            navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: uid } })
        },
        [navigate, org],
    )
    // Open a non-asset entity's detail page — the explicit open-in-new icon
    // only (EntityNode never renders it for `intervention`, which has no
    // detail route, so that kind never reaches here).
    const openEntity = useCallback(
        (kind: EntityKind, uid: string) => {
            if (kind === 'document') navigate({ to: '/$org/documents/$docUid', params: { org, docUid: uid } })
            else if (kind === 'expense') navigate({ to: '/$org/expenses/$expenseUid', params: { org, expenseUid: uid } })
            else if (kind === 'contract') navigate({ to: '/$org/contracts/$contractUid', params: { org, contractUid: uid } })
            else if (kind === 'contact') navigate({ to: '/$org/users/$userUid', params: { org, userUid: uid } })
        },
        [navigate, org],
    )
    const handleExpand = useCallback(
        (id: string) => {
            if (graph.nodes.length >= SOFT_CAP) {
                setCapOpen(true)
                return
            }
            if (expandedSet.has(id)) return
            const next = [...expandedArr, id]
            navigate({ search: { focus, expanded: next.join(',') } })
        },
        [graph.nodes.length, expandedSet, expandedArr, focus, navigate],
    )
    const handleCollapse = useCallback(
        (id: string) => {
            // Drop the asset expansion *and* any of its kind expansions (their
            // nodes hang off this asset and would otherwise be orphaned).
            const next = expandedArr.filter((tok) => {
                if (tok === id) return false
                const at = tok.indexOf('@')
                return !(at >= 0 && tok.slice(at + 1) === id)
            })
            navigate({ search: { focus, expanded: next.length ? next.join(',') : undefined } })
        },
        [expandedArr, focus, navigate],
    )
    // Toggle a non-asset kind for an asset (§4). Adds/removes `<abbr>@<uid>`.
    const handleToggleKind = useCallback(
        (assetUid: string, kind: EntityKind) => {
            const token = `${KIND_ABBR[kind]}@${assetUid}`
            if (expandedArr.includes(token)) {
                const next = expandedArr.filter((tok) => tok !== token)
                navigate({ search: { focus, expanded: next.length ? next.join(',') : undefined } })
                return
            }
            if (graph.nodes.length >= SOFT_CAP) {
                setCapOpen(true)
                return
            }
            navigate({ search: { focus, expanded: [...expandedArr, token].join(',') } })
        },
        [expandedArr, focus, graph.nodes.length, navigate],
    )
    // Toggle an intervention's expenses (nested expansion, `ivexp@<uid>`).
    const handleToggleIvExpenses = useCallback(
        (ivUid: string) => {
            const token = `${IVEXP}@${ivUid}`
            if (expandedArr.includes(token)) {
                const next = expandedArr.filter((tok) => tok !== token)
                navigate({ search: { focus, expanded: next.length ? next.join(',') : undefined } })
                return
            }
            if (graph.nodes.length >= SOFT_CAP) {
                setCapOpen(true)
                return
            }
            navigate({ search: { focus, expanded: [...expandedArr, token].join(',') } })
        },
        [expandedArr, focus, graph.nodes.length, navigate],
    )
    // Toggle a document/expense/contract/contact's own neighbor expansion
    // (§1, `<entAbbr>@<uid>`). No-op for a kind with no ENTITY_EXPAND_ABBR
    // entry (currently none — every kind resolves something).
    const handleToggleEntityNeighbors = useCallback(
        (kind: EntityKind, uid: string) => {
            const abbr = ENTITY_EXPAND_ABBR[kind]
            if (!abbr) return
            const token = `${abbr}@${uid}`
            if (expandedArr.includes(token)) {
                const next = expandedArr.filter((tok) => tok !== token)
                navigate({ search: { focus, expanded: next.length ? next.join(',') : undefined } })
                return
            }
            if (graph.nodes.length >= SOFT_CAP) {
                setCapOpen(true)
                return
            }
            navigate({ search: { focus, expanded: [...expandedArr, token].join(',') } })
        },
        [expandedArr, focus, graph.nodes.length, navigate],
    )
    // Single dispatch point for an EntityNode's body click / (+)(−) button /
    // Enter-Space (§1): interventions keep their existing ivexp→expenses
    // toggle, every other kind uses its own-neighbor expansion.
    const handleEntityToggle = useCallback(
        (kind: EntityKind, uid: string) => {
            if (kind === 'intervention') {
                handleToggleIvExpenses(uid)
                return
            }
            handleToggleEntityNeighbors(kind, uid)
        },
        [handleToggleIvExpenses, handleToggleEntityNeighbors],
    )
    // Body click on an asset node = expand/collapse ALL of its child kinds at
    // once (§3): the union of the (+) neighbor expansion and every per-kind
    // toggle. When the node is already fully expanded, a body click collapses
    // everything back (toggle). Never navigates — only the link icon does.
    const handleExpandAllKinds = useCallback(
        (assetUid: string) => {
            const isFocal = focusKind === 'asset' && assetUid === focusUid
            const assetExpanded = isFocal || expandedSet.has(assetUid)
            const kindTokens = ALL_KINDS.map((k) => `${KIND_ABBR[k]}@${assetUid}`)
            const allKindsPresent = kindTokens.every((tok) => expandedArr.includes(tok))
            if (assetExpanded && allKindsPresent) {
                // Fully expanded → collapse the asset token and every token that
                // hangs off this asset (its kind expansions).
                const next = expandedArr.filter((tok) => {
                    if (tok === assetUid) return false
                    const at = tok.indexOf('@')
                    return !(at >= 0 && tok.slice(at + 1) === assetUid)
                })
                navigate({ search: { focus, expanded: next.length ? next.join(',') : undefined } })
                return
            }
            if (graph.nodes.length >= SOFT_CAP) {
                setCapOpen(true)
                return
            }
            const additions: string[] = []
            if (!assetExpanded) additions.push(assetUid)
            for (const tok of kindTokens) if (!expandedArr.includes(tok)) additions.push(tok)
            if (additions.length === 0) return
            navigate({ search: { focus, expanded: [...expandedArr, ...additions].join(',') } })
        },
        [focus, focusKind, focusUid, expandedSet, expandedArr, graph.nodes.length, navigate],
    )
    // Body click on an asset node (§2): the already-focal node keeps the
    // expand-all/collapse-all toggle (handleExpandAllKinds) — the affordance
    // for "show me everything under this asset" would otherwise be lost. Any
    // OTHER asset becomes the new focal node instead (Open Question: default
    // to focus-only, not focus+expand-all — focusing already seeds the
    // asset's direct neighbors, and a double action would be hard to undo).
    const handleNodeClick = useCallback(
        (node: RFNode) => {
            const data = node.data as unknown as AssetNodeData
            const g = data?.graph
            if (!g || g.kind !== 'asset' || !g.assetUid) return
            const isFocal = focusKind === 'asset' && g.assetUid === focusUid
            if (isFocal) {
                handleExpandAllKinds(g.assetUid)
                return
            }
            setFocusFromNodeClick(g.assetUid)
        },
        [focusKind, focusUid, handleExpandAllKinds, setFocusFromNodeClick],
    )

    // Persist positions between derivations so incremental expansions don't
    // reshuffle the whole canvas. Layout runs in an effect (not during render)
    // and seeds from the previous positions kept in a ref. pinnedRef holds the
    // ids the user has manually dragged — those stay put across expansions and
    // are only released by the Re-layout control (which bumps relayoutToken).
    const positionsRef = useRef<Positions>({})
    const pinnedRef = useRef<Set<string>>(new Set())
    const appliedTokenRef = useRef(0)
    const [positions, setPositions] = useState<Positions>({})
    const [relayoutToken, setRelayoutToken] = useState(0)
    useEffect(() => {
        const nodeIds = graph.nodes.map((n) => n.id)
        const nextKeys = [...nodeIds].sort().join('|')
        const prevKeys = Object.keys(positionsRef.current).sort().join('|')
        const tokenChanged = appliedTokenRef.current !== relayoutToken
        // Skip when neither the node set nor the re-layout token changed —
        // avoids a setState→render→setState loop when upstream memo inputs churn.
        if (!tokenChanged && prevKeys === nextKeys) return
        // Full layered rebuild on first render or an explicit Re-layout; otherwise
        // an incremental pass that keeps existing (and pinned) nodes in place and
        // only positions the new ones near their source.
        const mode: 'full' | 'incremental' =
            tokenChanged || Object.keys(positionsRef.current).length === 0
                ? 'full'
                : 'incremental'
        const pos = computeGraphLayout({
            nodeIds,
            edges: graph.edges,
            focusId: focalNodeId,
            previous: positionsRef.current,
            pinned: pinnedRef.current,
            mode,
        })
        appliedTokenRef.current = relayoutToken
        positionsRef.current = pos
        setPositions(pos)
    }, [graph, focalNodeId, relayoutToken])

    // Controlled position updates. React Flow reports drag positions already in
    // flow coordinates (it applies the inverse pan/zoom transform for us), so
    // writing them straight back into `positions` keeps the grabbed node under
    // the pointer 1:1 at every zoom level — without this, the controlled
    // `position` prop snaps the node back on each re-render and it drifts (§4).
    const handleNodesChange = useCallback((changes: NodeChange[]) => {
        let touched = false
        const next = { ...positionsRef.current }
        for (const c of changes) {
            if (c.type === 'position' && c.position) {
                next[c.id] = c.position
                touched = true
            }
        }
        if (!touched) return
        positionsRef.current = next
        setPositions(next)
    }, [])

    // Record a manual drag: pin the node and keep its dragged position (so a
    // later expansion lays new nodes around it without clobbering the placement).
    const handleNodeDragStop = useCallback((id: string, pos: { x: number; y: number }) => {
        pinnedRef.current = new Set(pinnedRef.current).add(id)
        positionsRef.current = { ...positionsRef.current, [id]: pos }
        setPositions((p) => ({ ...p, [id]: pos }))
    }, [])

    // Re-layout: drop all manual pins and rebuild the full layered layout.
    const handleRelayout = useCallback(() => {
        pinnedRef.current = new Set()
        setRelayoutToken((tok) => tok + 1)
    }, [])

    // Map each node to its immediate hierarchy parent (for the parent chevron).
    const parentByChild = useMemo(() => {
        const m = new Map<string, string>()
        for (const e of graph.edges) {
            if (e.category === 'hierarchy' && !m.has(e.target)) m.set(e.target, e.source)
        }
        return m
    }, [graph.edges])

    // Build React Flow nodes/edges.
    const rfNodes: RFNode[] = useMemo(
        () =>
            graph.nodes.map((n) => {
                // Non-asset entity nodes use the smaller EntityNode component.
                if (n.kind !== 'asset' && n.kind !== 'more') {
                    const kind = n.kind as EntityKind
                    const canToggle = kind === 'intervention' || !!ENTITY_EXPAND_ABBR[kind]
                    const expanded = !n.entityUid
                        ? false
                        : kind === 'intervention'
                          ? expandedIvExpenses.has(n.entityUid)
                          : (expandedEntityNeighbors.get(kind)?.has(n.entityUid) ?? false)
                    const loading = !n.entityUid
                        ? false
                        : kind === 'intervention'
                          ? loadingIds.has(`ivexp:${n.entityUid}`)
                          : loadingIds.has(`en:${kind}:${n.entityUid}`)
                    const data: EntityNodeData = {
                        graph: n,
                        loading,
                        canToggle,
                        expanded,
                        onToggle: handleEntityToggle,
                        // Interventions have no detail route — no open icon.
                        onOpen: kind === 'intervention' ? undefined : openEntity,
                    }
                    return {
                        id: n.id,
                        type: 'entity',
                        position: positions[n.id] ?? { x: 0, y: 0 },
                        data: data as unknown as Record<string, unknown>,
                        draggable: true,
                    }
                }
                const parentUid = parentByChild.get(n.id)
                const data: AssetNodeData = {
                    graph: n,
                    isFocal: n.id === focalNodeId,
                    isExpanded: n.id === focalNodeId || expandedSet.has(n.id),
                    loading: loadingIds.has(n.id) || (n.moreParentUid ? loadingIds.has(n.moreParentUid) : false),
                    apiBaseUrl,
                    parentUid,
                    parentExpanded: parentUid ? parentUid === focalNodeId || expandedSet.has(parentUid) : false,
                    expandedKinds: n.assetUid ? expandedKindMap.get(n.assetUid) : undefined,
                    onExpand: handleExpand,
                    onCollapse: handleCollapse,
                    onOpen: openAsset,
                    onLoadMore: handleLoadMore,
                    onLoadMoreKind: handleLoadMoreKind,
                    onToggleKind: handleToggleKind,
                }
                return {
                    id: n.id,
                    type: 'asset',
                    position: positions[n.id] ?? { x: 0, y: 0 },
                    data: data as unknown as Record<string, unknown>,
                    draggable: true,
                }
            }),
        [graph.nodes, positions, focalNodeId, expandedSet, expandedKindMap, expandedIvExpenses, expandedEntityNeighbors, parentByChild, loadingIds, apiBaseUrl, handleExpand, handleCollapse, openAsset, openEntity, handleLoadMore, handleLoadMoreKind, handleToggleKind, handleEntityToggle],
    )

    const rfEdges: RFEdge[] = useMemo(
        () =>
            graph.edges.map((e) => ({
                id: e.id,
                source: e.source,
                target: e.target,
                label: e.category === 'relation' ? e.label : undefined,
                markerEnd:
                    e.category === 'relation' ? { type: MarkerType.ArrowClosed } : undefined,
                style:
                    e.category === 'hierarchy'
                        ? { strokeDasharray: '4 4', stroke: 'var(--muted-foreground)' }
                        : { stroke: 'var(--foreground)' },
                labelStyle: { fill: 'var(--foreground)', fontSize: 11 },
                labelBgStyle: { fill: 'var(--background)' },
            })),
        [graph.edges],
    )

    // Header search box (cmdk) — pick a focal asset directly from the graph page.
    const [searchQuery, setSearchQuery] = useState('')
    const debounced = useDebounce(searchQuery, 300)
    const [results, setResults] = useState<Array<{ uid: string; name: string }>>([])
    const [searchOpen, setSearchOpen] = useState(false)
    useEffect(() => {
        if (!debounced.trim()) {
            setResults([])
            return
        }
        let cancelled = false
        void (async () => {
            try {
                const res = (await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                    params: { path: { orgCode: org }, query: { q: debounced, limit: 10, asset_chain: true } },
                })) as {
                    data?: Array<{ uid: string; name?: string; parent_asset_uid?: string | null; parent_chain?: boolean }>
                }
                if (!cancelled) {
                    const rows = res.data ?? []
                    const byUid = new Map(rows.map((a) => [a.uid, a]))
                    setResults(
                        rows
                            .filter((a) => !a.parent_chain)
                            .map((a) => ({ uid: a.uid, name: formatAssetWithPath(a, byUid) })),
                    )
                }
            } catch {
                if (!cancelled) setResults([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [debounced, org, request])

    const searchBox = (
        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="graph-search-trigger">
                    <AppIcon path={mdiMagnify} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('search.placeholder')}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={t('search.placeholder')}
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                        data-testid="graph-search-input"
                    />
                    <CommandList>
                        <CommandEmpty>{t('search.empty')}</CommandEmpty>
                        <CommandGroup>
                            {results.map((r) => (
                                <CommandItem
                                    key={r.uid}
                                    value={r.uid}
                                    data-testid={`graph-search-result-${r.uid}`}
                                    onSelect={() => {
                                        setSearchOpen(false)
                                        setSearchQuery('')
                                        setFocusFromSearch(r.uid)
                                    }}
                                >
                                    {r.name}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )

    return (
        <div className="flex h-[calc(100vh-4rem)] flex-col">
            <PageHeader
                className="border-b border-border px-4 py-3"
                icon={SECTION_ICONS.graph}
                data-testid="graph-title"
                title={t('title')}
                description={
                    <span className="hidden md:block">{t('subtitle')}</span>
                }
                actions={searchBox}
            />

            <div className="relative flex-1" data-testid="graph-canvas">
                {focus ? (
                    <ReactFlowProvider>
                        <GraphCanvas
                            nodes={rfNodes}
                            edges={rfEdges}
                            focalNodeId={focalNodeId}
                            focalContentReady={focalContentReady}
                            relayoutToken={relayoutToken}
                            onNodeClick={handleNodeClick}
                            onNodesChange={handleNodesChange}
                            onNodeDragStop={handleNodeDragStop}
                            onRelayout={handleRelayout}
                        />
                    </ReactFlowProvider>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
                        <AppIcon tone="muted" path={mdiGraphOutline} size="2xl" />
                        <div>
                            <p className="text-lg font-medium">{t('empty.title')}</p>
                            <p className="text-sm text-muted-foreground">{t('empty.subtitle')}</p>
                        </div>
                        {searchBox}
                    </div>
                )}
            </div>

            <AlertDialog open={capOpen} onOpenChange={setCapOpen}>
                <AlertDialogContent data-testid="graph-cap-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('cap.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('cap.description', { count: SOFT_CAP })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction data-testid="graph-cap-ok">
                            {t('cap.ok')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
